import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdPhoneAndroid,
  MdPhoneIphone,
  MdPeopleAlt,
  MdSearch,
  MdRefresh,
  MdFileDownload,
  MdVisibility,
  MdClose,
  MdPerson,
  MdLogout,
  MdContentCopy,
  MdCheck,
  MdBluetoothConnected,
  MdBluetoothDisabled,
  MdCalendarToday,
  MdLocationOn,
  MdEmail,
  MdPhone,
  MdFingerprint,
  MdNotifications,
  MdClear,
  MdDeleteOutline,
} from "react-icons/md";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";
import ShowSnacker from "./ShowSnacker";
import ErrorPopup from "./ErrorPopup";
import "../../styles/DashBoard_AppUsers.css";

export interface AppUser {
  emailId: string;
  name: string;
  mobileNumber: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipCode?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  macId?: string | null;
  fcmToken?: string | null;
  os?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AppUsersStats {
  total: number;
  androidCount: number;
  iosCount: number;
  otherOsCount: number;
  pairedCount: number;
  unpairedCount: number;
  todaySignups: number;
  monthSignups: number;
}

function DashBoardAppUsers() {
  const navigate = useNavigate();

  // Data State
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [stats, setStats] = useState<AppUsersStats>({
    total: 0,
    androidCount: 0,
    iosCount: 0,
    otherOsCount: 0,
    pairedCount: 0,
    unpairedCount: 0,
    todaySignups: 0,
    monthSignups: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [serverError, setServerError] = useState<string | null>(null);

  // Filters State
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [osFilter, setOsFilter] = useState<string>("ALL");
  const [pairingFilter, setPairingFilter] = useState<string>("ALL");
  const [timeFilter, setTimeFilter] = useState<string>("ALL"); // "ALL" | "TODAY" | "MONTH"

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Modal & Detail State
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [copiedMac, setCopiedMac] = useState<string | null>(null);
  const [copiedFcm, setCopiedFcm] = useState<boolean>(false);

  // Role & Permissions
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canDeleteUser = hasPermission("canDeleteUser", userRole);

  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);
    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  // Delete State
  const [deleteCandidate, setDeleteCandidate] = useState<AppUser | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Snackbar Notification State
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });
  const showNotification = (message: string, type: "info" | "success" | "warning" | "danger" = "info") => {
    setSnack({ open: true, message, type });
  };

  // Fetch App Users Data
  const fetchAppUsers = useCallback(async () => {
    setLoading(true);
    setServerError(null);
    try {
      const response = await apiFetch("/user/all", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load app users (Status: ${response.status})`);
      }

      const data = await response.json();
      let rawList: AppUser[] = [];

      if (Array.isArray(data)) {
        rawList = data;
      } else if (data && Array.isArray(data.users)) {
        rawList = data.users;
      }

      setAppUsers(rawList);

      // Extract stats if provided by server, or calculate locally
      if (data && data.stats) {
        setStats(data.stats);
      } else {
        const total = rawList.length;
        let android = 0;
        let ios = 0;
        let paired = 0;
        let unpaired = 0;
        let today = 0;
        let month = 0;

        const now = new Date();
        const todayStr = now.toISOString().slice(0, 10);
        const curMonth = now.getMonth();
        const curYear = now.getFullYear();

        rawList.forEach((u) => {
          const osLower = (u.os || "").toLowerCase();
          if (osLower.includes("android")) android++;
          else if (osLower.includes("ios") || osLower.includes("iphone") || osLower.includes("apple")) ios++;

          if (u.macId && u.macId.trim() !== "") paired++;
          else unpaired++;

          if (u.createdAt) {
            const d = new Date(u.createdAt);
            if (!isNaN(d.getTime())) {
              if (d.toISOString().slice(0, 10) === todayStr) today++;
              if (d.getMonth() === curMonth && d.getFullYear() === curYear) month++;
            }
          }
        });

        setStats({
          total,
          androidCount: android,
          iosCount: ios,
          otherOsCount: Math.max(0, total - android - ios),
          pairedCount: paired,
          unpairedCount: unpaired,
          todaySignups: today,
          monthSignups: month,
        });
      }
    } catch (err: any) {
      console.error("Error fetching app users:", err);
      setServerError(err.message || "Failed to fetch application signup records.");
      showNotification("Could not fetch application users list", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppUsers();
  }, [fetchAppUsers]);

  const confirmDeleteUser = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      const targetId = deleteCandidate.emailId || deleteCandidate.mobileNumber;
      const res = await apiFetch(`/user/${encodeURIComponent(targetId)}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok) {
        showNotification(
          `Customer account ${deleteCandidate.name || deleteCandidate.emailId} deleted successfully from devicehub.studds.com`,
          "success"
        );
        setDeleteCandidate(null);
        if (selectedUser?.emailId === deleteCandidate.emailId) {
          setSelectedUser(null);
        }
        fetchAppUsers();
      } else {
        showNotification(data.message || "Failed to delete customer account", "danger");
      }
    } catch (err: any) {
      showNotification("Network error while deleting customer account", "danger");
    } finally {
      setIsDeleting(false);
    }
  };

  // Copy to clipboard helper
  const handleCopyMac = (mac: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(mac);
    setCopiedMac(mac);
    showNotification(`MAC ID "${mac}" copied to clipboard`, "success");
    setTimeout(() => setCopiedMac(null), 2500);
  };

  const handleCopyFcm = (token: string) => {
    navigator.clipboard.writeText(token);
    setCopiedFcm(true);
    showNotification("FCM Push Notification Token copied", "success");
    setTimeout(() => setCopiedFcm(false), 2500);
  };

  // Filter Logic
  const filteredUsers = useMemo(() => {
    return appUsers.filter((u) => {
      // 1. Search Query
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = (u.name || "").toLowerCase().includes(q);
        const emailMatch = (u.emailId || "").toLowerCase().includes(q);
        const phoneMatch = (u.mobileNumber || "").toLowerCase().includes(q);
        const macMatch = (u.macId || "").toLowerCase().includes(q);
        const cityMatch = (u.city || "").toLowerCase().includes(q);
        const stateMatch = (u.state || "").toLowerCase().includes(q);

        if (!nameMatch && !emailMatch && !phoneMatch && !macMatch && !cityMatch && !stateMatch) {
          return false;
        }
      }

      // 2. OS Filter
      if (osFilter !== "ALL") {
        const osLower = (u.os || "").toLowerCase();
        if (osFilter === "ANDROID" && !osLower.includes("android")) return false;
        if (osFilter === "IOS" && (!osLower.includes("ios") && !osLower.includes("iphone") && !osLower.includes("apple"))) return false;
        if (osFilter === "OTHER" && (osLower.includes("android") || osLower.includes("ios") || osLower.includes("iphone") || osLower.includes("apple"))) return false;
      }

      // 3. Pairing Filter
      if (pairingFilter !== "ALL") {
        const isPaired = Boolean(u.macId && u.macId.trim() !== "");
        if (pairingFilter === "PAIRED" && !isPaired) return false;
        if (pairingFilter === "UNPAIRED" && isPaired) return false;
      }

      // 4. Time Filter
      if (timeFilter !== "ALL" && u.createdAt) {
        const d = new Date(u.createdAt);
        const now = new Date();
        if (!isNaN(d.getTime())) {
          if (timeFilter === "TODAY") {
            if (d.toISOString().slice(0, 10) !== now.toISOString().slice(0, 10)) return false;
          } else if (timeFilter === "MONTH") {
            if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
          }
        }
      }

      return true;
    });
  }, [appUsers, searchQuery, osFilter, pairingFilter, timeFilter]);

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setOsFilter("ALL");
    setPairingFilter("ALL");
    setTimeFilter("ALL");
    setCurrentPage(1);
  };

  // Pagination Logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredUsers.length === 0) {
      showNotification("No records available to export", "warning");
      return;
    }

    const excelRows = filteredUsers.map((u, index) => ({
      "S.No": index + 1,
      "Full Name": u.name || "N/A",
      "Email Address": u.emailId || "N/A",
      "Mobile Number": u.mobileNumber || "N/A",
      "Platform / OS": u.os || "Unknown",
      "Paired Helmet MAC": u.macId || "Not Paired",
      "City": u.city || "N/A",
      "State": u.state || "N/A",
      "Country": u.country || "N/A",
      "Zip Code": u.zipCode || "N/A",
      "Registered On": u.createdAt ? new Date(u.createdAt).toLocaleString("en-IN") : "N/A",
      "Last Profile Update": u.updatedAt ? new Date(u.updatedAt).toLocaleString("en-IN") : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "App_Signups");

    // Auto-size columns
    const colWidths = [
      { wch: 8 },  // S.No
      { wch: 22 }, // Name
      { wch: 28 }, // Email
      { wch: 16 }, // Mobile
      { wch: 16 }, // OS
      { wch: 22 }, // MAC
      { wch: 16 }, // City
      { wch: 16 }, // State
      { wch: 14 }, // Country
      { wch: 12 }, // Zip
      { wch: 22 }, // Registered On
      { wch: 22 }, // Updated At
    ];
    worksheet["!cols"] = colWidths;

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Studds_App_Signups_${dateStr}.xlsx`);
    showNotification("App signup registrations report downloaded successfully", "success");
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.removeItem("userRole");
    navigate("/login");
  };

  // Format Helper for OS Badges
  const renderOsBadge = (os?: string | null) => {
    if (!os) {
      return (
        <span className="app-platform-badge unknown">
          <MdPhoneAndroid size={14} /> Unknown
        </span>
      );
    }
    const lower = os.toLowerCase();
    if (lower.includes("android")) {
      return (
        <span className="app-platform-badge android">
          <MdPhoneAndroid size={14} /> Android
        </span>
      );
    }
    if (lower.includes("ios") || lower.includes("iphone") || lower.includes("apple")) {
      return (
        <span className="app-platform-badge ios">
          <MdPhoneIphone size={14} /> iOS
        </span>
      );
    }
    return (
      <span className="app-platform-badge unknown">
        <MdPhoneAndroid size={14} /> {os}
      </span>
    );
  };

  // Format Helper for Date
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="app-users-container">
      {/* Top Header Row */}
      <div className="app-users-header-row">
        <div className="app-users-title-section">
          <h2>
            <MdPhoneAndroid className="title-icon" />
            App Signups
          </h2>
          <p>Complete customer registration directory for users downloading and signing up in the Studds mobile application</p>
        </div>

        <div className="app-users-actions-row">
          <button
            type="button"
            className="app-users-btn-export"
            onClick={handleExportExcel}
            title="Download App Signups Report as Excel"
          >
            <MdFileDownload size={18} />
            <span>Export Report</span>
          </button>

          <div className="app-users-round-icons">
            <span
              className="app-users-round-btn"
              onClick={fetchAppUsers}
              title="Refresh Data"
            >
              <MdRefresh size={19} />
            </span>
            <span
              className="app-users-round-btn"
              onClick={() => navigate("/dashboard/profile")}
              title="Admin Profile"
            >
              <MdPerson size={19} />
            </span>
            <span
              className="app-users-round-btn logout"
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={19} />
            </span>
          </div>
        </div>
      </div>

      {/* Top KPI Stat Cards Grid */}
      <div className="app-users-stats-grid">
        <div
          className={`app-stat-card ${osFilter === "ALL" && pairingFilter === "ALL" && timeFilter === "ALL" ? "active-card" : ""}`}
          onClick={handleResetFilters}
          title="Click to view all registrations"
        >
          <div className="app-stat-icon-wrap total">
            <MdPeopleAlt />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">Total Signups</span>
            <span className="app-stat-value">{stats.total}</span>
            <span className="app-stat-sub">Registered Customers</span>
          </div>
        </div>

        <div
          className={`app-stat-card ${osFilter === "ANDROID" ? "active-card" : ""}`}
          onClick={() => {
            setOsFilter("ANDROID");
            setTimeFilter("ALL");
            setCurrentPage(1);
          }}
          title="Filter by Android registrations"
        >
          <div className="app-stat-icon-wrap android">
            <MdPhoneAndroid />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">Android Users</span>
            <span className="app-stat-value">{stats.androidCount}</span>
            <span className="app-stat-sub">
              {stats.total > 0 ? `${((stats.androidCount / stats.total) * 100).toFixed(1)}% of total` : "0%"}
            </span>
          </div>
        </div>

        <div
          className={`app-stat-card ${osFilter === "IOS" ? "active-card" : ""}`}
          onClick={() => {
            setOsFilter("IOS");
            setTimeFilter("ALL");
            setCurrentPage(1);
          }}
          title="Filter by iOS registrations"
        >
          <div className="app-stat-icon-wrap ios">
            <MdPhoneIphone />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">iOS Users</span>
            <span className="app-stat-value">{stats.iosCount}</span>
            <span className="app-stat-sub">
              {stats.total > 0 ? `${((stats.iosCount / stats.total) * 100).toFixed(1)}% of total` : "0%"}
            </span>
          </div>
        </div>

        <div
          className={`app-stat-card ${pairingFilter === "PAIRED" ? "active-card" : ""}`}
          onClick={() => {
            setPairingFilter("PAIRED");
            setTimeFilter("ALL");
            setCurrentPage(1);
          }}
          title="Filter by users with paired smart helmets"
        >
          <div className="app-stat-icon-wrap paired">
            <MdBluetoothConnected />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">Helmets Paired</span>
            <span className="app-stat-value">{stats.pairedCount}</span>
            <span className="app-stat-sub">With Linked MAC ID</span>
          </div>
        </div>

        <div
          className={`app-stat-card ${pairingFilter === "UNPAIRED" ? "active-card" : ""}`}
          onClick={() => {
            setPairingFilter("UNPAIRED");
            setTimeFilter("ALL");
            setCurrentPage(1);
          }}
          title="Filter by users who have not paired a helmet"
        >
          <div className="app-stat-icon-wrap unpaired">
            <MdBluetoothDisabled />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">Unpaired Users</span>
            <span className="app-stat-value">{stats.unpairedCount}</span>
            <span className="app-stat-sub">No Helmet Linked</span>
          </div>
        </div>

        <div
          className={`app-stat-card ${timeFilter === "TODAY" ? "active-card" : ""}`}
          onClick={() => {
            setTimeFilter("TODAY");
            setCurrentPage(1);
          }}
          title="Filter by signups today"
        >
          <div className="app-stat-icon-wrap today">
            <MdCalendarToday />
          </div>
          <div className="app-stat-info">
            <span className="app-stat-label">Today's Signups</span>
            <span className="app-stat-value">{stats.todaySignups}</span>
            <span className="app-stat-sub">{stats.monthSignups} this month</span>
          </div>
        </div>
      </div>

      {/* Toolbar & Filter Bar */}
      <div className="app-users-toolbar-card">
        <div className="app-users-search-box">
          <MdSearch className="app-users-search-icon" />
          <input
            type="text"
            className="app-users-search-input"
            placeholder="Search by Name, Email, Mobile, MAC ID, City..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              className="app-users-search-clear"
              onClick={() => setSearchQuery("")}
            >
              <MdClear size={16} />
            </button>
          )}
        </div>

        <div className="app-users-filters-group">
          {/* OS Platform Filter */}
          <select
            className="app-users-select"
            value={osFilter}
            onChange={(e) => {
              setOsFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="ALL">All Platforms</option>
            <option value="ANDROID">Android</option>
            <option value="IOS">iOS</option>
            <option value="OTHER">Other / Unknown</option>
          </select>

          {/* Helmet Pairing Filter */}
          <select
            className="app-users-select"
            value={pairingFilter}
            onChange={(e) => {
              setPairingFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="ALL">All Pairing Status</option>
            <option value="PAIRED">Paired with Helmet</option>
            <option value="UNPAIRED">Unpaired</option>
          </select>

          {/* Time Filter */}
          <select
            className="app-users-select"
            value={timeFilter}
            onChange={(e) => {
              setTimeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="ALL">All Time</option>
            <option value="TODAY">Signed Up Today</option>
            <option value="MONTH">This Month</option>
          </select>

          {/* Clear Filters Button */}
          {(searchQuery || osFilter !== "ALL" || pairingFilter !== "ALL" || timeFilter !== "ALL") && (
            <button
              type="button"
              className="app-users-btn-clear"
              onClick={handleResetFilters}
            >
              <MdClear size={16} />
              <span>Clear</span>
            </button>
          )}

          <div className="app-users-count-tag">
            Showing {filteredUsers.length} of {appUsers.length} users
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="app-users-table-card">
        {loading ? (
          <div className="app-users-loading-box">
            <div className="app-users-spinner" />
            <p style={{ color: "#64748b", margin: 0 }}>Loading application signups...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="app-users-empty-box">
            <MdPhoneAndroid className="app-users-empty-icon" />
            <div className="app-users-empty-title">No App Registrations Found</div>
            <div className="app-users-empty-sub">
              {searchQuery || osFilter !== "ALL" || pairingFilter !== "ALL" || timeFilter !== "ALL"
                ? "No customer signups match the selected filter criteria."
                : "No customer signup registrations have been recorded yet."}
            </div>
            {(searchQuery || osFilter !== "ALL" || pairingFilter !== "ALL" || timeFilter !== "ALL") && (
              <button
                type="button"
                className="app-users-btn-clear"
                onClick={handleResetFilters}
                style={{ marginTop: "16px" }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="app-users-table-responsive">
              <table className="app-users-table">
                <thead>
                  <tr>
                    <th style={{ width: "50px", textAlign: "center" }}>#</th>
                    <th>User / Customer</th>
                    <th>Mobile Number</th>
                    <th>Platform</th>
                    <th>Paired Helmet MAC</th>
                    <th>Location</th>
                    <th>Registered On</th>
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user, idx) => {
                    const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                    const initial = (user.name || user.emailId || "U").charAt(0).toUpperCase();

                    return (
                      <tr key={user.emailId || idx}>
                        <td style={{ textAlign: "center", color: "#94a3b8", fontWeight: 600 }}>
                          {rowNumber}
                        </td>
                        <td>
                          <div className="app-user-cell">
                            {user.image || user.imageUrl ? (
                              <img
                                src={user.imageUrl || user.image || ""}
                                alt={user.name || "User"}
                                className="app-user-avatar"
                                onError={(e) => {
                                  // Fallback to placeholder if image fails to load
                                  (e.target as HTMLElement).style.display = "none";
                                  const fallback = (e.target as HTMLElement).nextElementSibling;
                                  if (fallback) (fallback as HTMLElement).style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className="app-user-avatar-placeholder"
                              style={{ display: user.image || user.imageUrl ? "none" : "flex" }}
                            >
                              {initial}
                            </div>
                            <div className="app-user-name-box">
                              <span className="app-user-name" title={user.name}>
                                {user.name || "Unnamed User"}
                              </span>
                              <span className="app-user-email" title={user.emailId}>
                                {user.emailId}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <MdPhone size={15} color="#94a3b8" />
                            <span style={{ fontWeight: 500 }}>{user.mobileNumber || "—"}</span>
                          </div>
                        </td>
                        <td>{renderOsBadge(user.os)}</td>
                        <td>
                          {user.macId && user.macId.trim() !== "" ? (
                            <div className="app-mac-badge-wrap">
                              <span className="app-mac-badge" title="Paired Smart Helmet">
                                <MdBluetoothConnected size={14} />
                                {user.macId}
                              </span>
                              <button
                                type="button"
                                className="app-mac-copy-btn"
                                onClick={(e) => handleCopyMac(user.macId!, e)}
                                title="Copy MAC ID"
                              >
                                {copiedMac === user.macId ? (
                                  <MdCheck size={15} color="#10b981" />
                                ) : (
                                  <MdContentCopy size={15} />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="app-mac-unpaired-badge">
                              <MdBluetoothDisabled size={14} /> Not Paired
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <MdLocationOn size={15} color="#94a3b8" />
                            <span>
                              {[user.city, user.state, user.country].filter(Boolean).join(", ") || "—"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span style={{ color: "#475569", fontSize: "12.5px" }}>
                            {formatDate(user.createdAt)}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                            <button
                              type="button"
                              className="app-view-details-btn"
                              onClick={() => setSelectedUser(user)}
                              title="View Full Registration Profile"
                            >
                              <MdVisibility size={16} />
                              <span>View</span>
                            </button>
                            {canDeleteUser && (
                              <button
                                type="button"
                                className="app-view-details-btn"
                                onClick={() => setDeleteCandidate(user)}
                                title="Delete Customer Account from devicehub.studds.com"
                                style={{
                                  background: "#fef2f2",
                                  color: "#dc2626",
                                  borderColor: "#fecaca",
                                }}
                              >
                                <MdDeleteOutline size={16} />
                                <span>Delete</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="app-users-pagination">
              <div className="app-users-page-info">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} entries
              </div>

              <div className="app-users-page-btns">
                <button
                  type="button"
                  className="app-users-page-btn"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .map((pageNum, idx, arr) => {
                    const prevVal = arr[idx - 1];
                    const showEllipsis = prevVal && pageNum - prevVal > 1;

                    return (
                      <React.Fragment key={pageNum}>
                        {showEllipsis && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}
                        <button
                          type="button"
                          className={`app-users-page-btn ${currentPage === pageNum ? "active" : ""}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  type="button"
                  className="app-users-page-btn"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View User Profile Modal */}
      {selectedUser && (
        <div className="app-modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="app-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="app-modal-header">
              <div className="app-modal-header-title">
                <MdPeopleAlt size={22} color="#e60000" />
                <h3>Customer Signup Details</h3>
              </div>
              <button
                type="button"
                className="app-modal-close-btn"
                onClick={() => setSelectedUser(null)}
              >
                <MdClose size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="app-modal-body">
              {/* User Profile Hero */}
              <div className="app-modal-user-hero">
                {selectedUser.image || selectedUser.imageUrl ? (
                  <img
                    src={selectedUser.imageUrl || selectedUser.image || ""}
                    alt={selectedUser.name || "User"}
                    className="app-modal-hero-avatar"
                  />
                ) : (
                  <div className="app-modal-hero-avatar-placeholder">
                    {(selectedUser.name || selectedUser.emailId || "U").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="app-modal-hero-info">
                  <span className="app-modal-hero-name">{selectedUser.name || "Customer"}</span>
                  <span className="app-modal-hero-sub">{selectedUser.emailId}</span>
                  <div style={{ marginTop: "4px" }}>
                    {renderOsBadge(selectedUser.os)}
                  </div>
                </div>
              </div>

              {/* Personal & Contact Section */}
              <div>
                <div className="app-modal-section-title">
                  <MdPerson size={16} /> Contact & Personal Details
                </div>
                <div className="app-modal-grid">
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Full Name</span>
                    <span className="app-modal-field-value">{selectedUser.name || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Email ID</span>
                    <span className="app-modal-field-value">{selectedUser.emailId || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Mobile Number</span>
                    <span className="app-modal-field-value">{selectedUser.mobileNumber || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Operating System</span>
                    <span className="app-modal-field-value">{selectedUser.os || "Unknown"}</span>
                  </div>
                </div>
              </div>

              {/* Geographical Section */}
              <div>
                <div className="app-modal-section-title">
                  <MdLocationOn size={16} /> Location & Address
                </div>
                <div className="app-modal-grid">
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">City</span>
                    <span className="app-modal-field-value">{selectedUser.city || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">State</span>
                    <span className="app-modal-field-value">{selectedUser.state || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Country</span>
                    <span className="app-modal-field-value">{selectedUser.country || "—"}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Postal / Zip Code</span>
                    <span className="app-modal-field-value">{selectedUser.zipCode || "—"}</span>
                  </div>
                </div>
              </div>

              {/* Smart Helmet & Hardware Section */}
              <div>
                <div className="app-modal-section-title">
                  <MdFingerprint size={16} /> Linked Smart Helmet Device
                </div>
                <div className="app-modal-grid">
                  <div className="app-modal-field" style={{ gridColumn: "span 2" }}>
                    <span className="app-modal-field-label">Paired MAC ID</span>
                    <span className="app-modal-field-value">
                      {selectedUser.macId && selectedUser.macId.trim() !== "" ? (
                        <>
                          <span style={{ fontFamily: "monospace", color: "#1d4ed8", fontSize: "15px" }}>
                            {selectedUser.macId}
                          </span>
                          <button
                            type="button"
                            className="app-mac-copy-btn"
                            onClick={() => handleCopyMac(selectedUser.macId!)}
                            title="Copy MAC ID"
                          >
                            {copiedMac === selectedUser.macId ? (
                              <MdCheck size={16} color="#10b981" />
                            ) : (
                              <MdContentCopy size={16} />
                            )}
                          </button>
                        </>
                      ) : (
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}>No Smart Helmet Paired Yet</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* App & Device Tokens Section */}
              <div>
                <div className="app-modal-section-title">
                  <MdNotifications size={16} /> Mobile App & Notification Metadata
                </div>
                <div className="app-modal-grid">
                  <div className="app-modal-field" style={{ gridColumn: "span 2" }}>
                    <span className="app-modal-field-label">FCM Push Notification Token</span>
                    <span className="app-modal-field-value">
                      {selectedUser.fcmToken ? (
                        <>
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: "12px",
                              color: "#475569",
                              maxWidth: "500px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={selectedUser.fcmToken}
                          >
                            {selectedUser.fcmToken}
                          </span>
                          <button
                            type="button"
                            className="app-mac-copy-btn"
                            onClick={() => handleCopyFcm(selectedUser.fcmToken!)}
                            title="Copy FCM Token"
                          >
                            {copiedFcm ? (
                              <MdCheck size={16} color="#10b981" />
                            ) : (
                              <MdContentCopy size={16} />
                            )}
                          </button>
                        </>
                      ) : (
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}>No FCM Token available</span>
                      )}
                    </span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Registration Date</span>
                    <span className="app-modal-field-value">{formatDate(selectedUser.createdAt)}</span>
                  </div>
                  <div className="app-modal-field">
                    <span className="app-modal-field-label">Last Profile Update</span>
                    <span className="app-modal-field-value">{formatDate(selectedUser.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="app-modal-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {canDeleteUser ? (
                <button
                  type="button"
                  onClick={() => setDeleteCandidate(selectedUser)}
                  style={{
                    background: "#dc2626",
                    color: "#ffffff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  title="Delete Customer Account and remove details from devicehub.studds.com"
                >
                  <MdDeleteOutline size={17} />
                  <span>Delete Customer Account</span>
                </button>
              ) : <div />}
              <button
                type="button"
                className="app-modal-btn-close"
                onClick={() => setSelectedUser(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal: Delete Customer Account from devicehub.studds.com */}
      {deleteCandidate && (
        <div className="app-modal-overlay" onClick={() => !isDeleting && setDeleteCandidate(null)}>
          <div
            className="app-modal-container"
            style={{ maxWidth: "520px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="app-modal-header"
              style={{ background: "#fee2e2", borderBottom: "1px solid #fecaca" }}
            >
              <div className="app-modal-header-title">
                <MdDeleteOutline size={22} color="#dc2626" />
                <h3 style={{ color: "#991b1b" }}>Delete Customer Account</h3>
              </div>
              <button
                type="button"
                className="app-modal-close-btn"
                disabled={isDeleting}
                onClick={() => setDeleteCandidate(null)}
              >
                <MdClose size={20} />
              </button>
            </div>

            <div className="app-modal-body" style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 14px 0", fontSize: "14px", color: "#334155", lineHeight: "1.5" }}>
                Are you sure you want to delete customer account for{" "}
                <strong>{deleteCandidate.name || deleteCandidate.emailId}</strong>?
              </p>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "14px",
                  fontSize: "13px",
                  color: "#475569",
                }}
              >
                <div><strong>Email ID:</strong> {deleteCandidate.emailId}</div>
                <div style={{ marginTop: "4px" }}><strong>Mobile Number:</strong> {deleteCandidate.mobileNumber || "—"}</div>
                {deleteCandidate.macId && (
                  <div style={{ marginTop: "4px" }}><strong>Paired Helmet MAC:</strong> {deleteCandidate.macId}</div>
                )}
                {deleteCandidate.os && (
                  <div style={{ marginTop: "4px" }}><strong>Platform:</strong> {deleteCandidate.os}</div>
                )}
              </div>
              <p style={{ margin: "14px 0 0 0", fontSize: "12.5px", color: "#b91c1c", fontWeight: 500, lineHeight: "1.5" }}>
                ⚠️ This action will permanently remove all registration records, profile pictures, warranty registrations, uploaded invoice receipts, and support tickets from devicehub.studds.com.
              </p>
            </div>

            <div
              className="app-modal-footer"
              style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "14px 20px" }}
            >
              <button
                type="button"
                className="app-modal-btn-close"
                disabled={isDeleting}
                onClick={() => setDeleteCandidate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={confirmDeleteUser}
                style={{
                  background: "#dc2626",
                  color: "#ffffff",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete Customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Error Popup if Server Error occurs */}
      {serverError && (
        <ErrorPopup
          error={serverError}
          onClose={() => setServerError(null)}
        />
      )}

      {/* Toast Notification */}
      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type}
        handleClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}

export default DashBoardAppUsers;

